import React, { useEffect, useState } from "react";
import { api, formatIDR } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const ROASTS = ["light", "medium", "dark"];
const empty = { name: "", origin: "", roast_profile: "medium", description_id: "", description_en: "", weight_grams: 250, price: 0, stock: 0, image_url: "", tasting_notes: [], active: true };

export default function ProductManagement() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/products").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const startEdit = (it) => { setEditing(it); setForm({ ...it, tasting_notes: it.tasting_notes || [] }); setOpen(true); };

  const save = async () => {
    const payload = { ...form,
      price: Number(form.price), stock: Number(form.stock), weight_grams: Number(form.weight_grams),
      tasting_notes: typeof form.tasting_notes === "string" ? form.tasting_notes.split(",").map((s) => s.trim()).filter(Boolean) : form.tasting_notes };
    if (editing) await api.put(`/products/${editing.id}`, payload);
    else await api.post("/products", payload);
    toast.success("Saved");
    setOpen(false); load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/products/${id}`); toast.success("Deleted"); load();
  };

  const uploadImage = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setForm((f) => ({ ...f, image_url: `${process.env.REACT_APP_BACKEND_URL}${r.data.url}` }));
    toast.success("Uploaded");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold" style={{ fontFamily: "Outfit" }}>Bean Products</h1>
        <Button onClick={startNew} data-testid="product-new-btn"><Plus size={14} className="mr-2" />New Product</Button>
      </div>
      <Card className="border-zinc-200">
        <CardContent className="p-0">
          <Table data-testid="products-mgmt-table">
            <TableHeader><TableRow>
              <TableHead>Image</TableHead><TableHead>Name</TableHead><TableHead>Origin</TableHead>
              <TableHead>Roast</TableHead><TableHead>Price</TableHead><TableHead>Stock</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map((it) => (
                <TableRow key={it.id} data-testid={`product-row-${it.id}`}>
                  <TableCell><div className="w-10 h-10 rounded bg-zinc-100 overflow-hidden">{it.image_url && <img src={it.image_url} alt="" className="w-full h-full object-cover" />}</div></TableCell>
                  <TableCell>{it.name}</TableCell>
                  <TableCell>{it.origin}</TableCell>
                  <TableCell className="capitalize">{it.roast_profile}</TableCell>
                  <TableCell className="font-mono">{formatIDR(it.price)}</TableCell>
                  <TableCell className="font-mono">{it.stock}</TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="icon" variant="ghost" onClick={() => startEdit(it)} data-testid={`product-edit-${it.id}`}><Pencil size={14} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => del(it.id)} data-testid={`product-del-${it.id}`}><Trash size={14} /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? "Edit Product" : "New Product"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pf-name" /></div>
              <div><Label>Origin</Label><Input value={form.origin} onChange={(e) => setForm({ ...form, origin: e.target.value })} data-testid="pf-origin" /></div>
            </div>
            <div><Label>Description (ID)</Label><Textarea value={form.description_id} onChange={(e) => setForm({ ...form, description_id: e.target.value })} /></div>
            <div><Label>Description (EN)</Label><Textarea value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })} /></div>
            <div className="grid grid-cols-4 gap-3">
              <div><Label>Roast</Label>
                <Select value={form.roast_profile} onValueChange={(v) => setForm({ ...form, roast_profile: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{ROASTS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Weight (g)</Label><Input type="number" value={form.weight_grams} onChange={(e) => setForm({ ...form, weight_grams: e.target.value })} /></div>
              <div><Label>Price (IDR)</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="pf-price" /></div>
              <div><Label>Stock</Label><Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="pf-stock" /></div>
            </div>
            <div><Label>Image URL</Label><Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://..." /></div>
            <div><Label>Or upload</Label><Input type="file" accept="image/*" onChange={(e) => e.target.files[0] && uploadImage(e.target.files[0])} /></div>
            <div><Label>Tasting Notes (comma separated)</Label><Input value={Array.isArray(form.tasting_notes) ? form.tasting_notes.join(", ") : form.tasting_notes} onChange={(e) => setForm({ ...form, tasting_notes: e.target.value })} /></div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} data-testid="pf-save">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
